// SceneryTiming — a lightweight high-level timing accumulator for the scenery placement/render
// pipeline. PURPOSE (2026-08-28 debug session): the scenery load at extreme zoom-out was crawling
// (~dozens of seconds), and a full profiler trace showed `distToPolyline` (14 s) + `regionGeoFactorAt`
// (10 s) dominating — both called from `placeSceneryTile`'s per-lattice-cell region-geometry
// evaluation. This module adds cheap, named accumulators around the pipeline's hot stages so an
// optimizer can see where time goes frame-to-frame WITHOUT a full profiler run.
//
// DESIGN:
//  - Per-stage cumulative microseconds + call counts, reset-able.
//  - Thread-safe enough for the single-threaded browser main thread (the placer runs synchronously).
//  - A `now()` helper (performance.now) with a monotonic fallback for Node test envs.
//  - Auto-flush to the console every N calls (see `autoFlushEvery`) so it stays visible during a
//    long load without manual intervention; each flush is cumulative-since-last-flush.
//  - Zero-cost when disabled (`enabled = false` — every timer no-ops).
//
// CONSUMPTION: `window.lfSceneryTiming.dump()` returns a snapshot of cumulative times;
// `window.lfSceneryTiming.reset()` clears them. The agent can read it via the browser console.
// In tests/Node, import the module directly and read the same `timing` object.

export interface TimingBucket {
	/** Cumulative self-time across calls, microseconds. */
	us: number;
	/** Number of calls recorded. */
	calls: number;
	/** Cumulative self-time across calls, ms (convenience). */
	ms: number;
}

const buckets: Record<string, TimingBucket> = {};

/** Enable/disable all timing (default ON in DEV builds; set false to strip all overhead). */
export const sceneryTimingEnabled = true;

/** Flush (console.log + reset) every N total calls, to keep a long load's output bounded. 0 = never
 *  auto-flush (only via `dump()`/`flush()`). */
let autoFlushEvery = 0;
let totalCalls = 0;

function bucket(name: string): TimingBucket {
	let b = buckets[name];
	if (!b) { b = {us: 0, calls: 0, ms: 0}; buckets[name] = b; }
	return b;
}

/** The monotonic clock. performance.now exists in browsers + modern Node; fall back to Date.now. */
export function timingNow(): number {
	const p = (globalThis as {performance?: {now(): number}}).performance;
	return p ? p.now() : Date.now();
}

/** Run `fn` and accumulate its self-time into the named bucket. Returns fn's result. */
export function timeIt<T>(name: string, fn: () => T): T {
	if (!sceneryTimingEnabled) return fn();
	const t0 = timingNow();
	try {
		return fn();
	} finally {
		const dt = (timingNow() - t0) * 1000;
		const b = bucket(name);
		b.us += dt;
		b.calls++;
		b.ms = b.us / 1000;
		if (autoFlushEvery > 0 && (++totalCalls % autoFlushEvery) == 0) flush();
	}
}

/** Record an explicit delta (us) + 1 call into the named bucket (for async / manual timing). */
export function recordTime(name: string, us: number): void {
	if (!sceneryTimingEnabled) return;
	const b = bucket(name);
	b.us += us;
	b.calls++;
	b.ms = b.us / 1000;
}

// ── Per-statement diagnostics (2026-08-28) ─────────────────────────────────────────
// V8 folds an anonymous closure's self-time into ONE profile node (the first statement of the
// arrow body), so a profiler trace CANNOT split which statement inside `renderKind.tileLoop`
// dominates. This helper lets the loop accumulate each statement's wall-time into its own bucket
// (named via `timeStmt("label", fn)`), so we can see the true per-statement split from
// `window.lfSceneryTiming.dump()` WITHOUT a profiler. Zero-cost when disabled; cheap (~µs) when on.

export function timeStmt<T>(name: string, fn: () => T): T {
	if (!sceneryTimingEnabled) return fn();
	const t0 = timingNow();
	try { return fn(); }
	finally {
		const b = bucket(`stmt.${name}`);
		b.us += (timingNow() - t0) * 1000;
		b.calls++;
		b.ms = b.us / 1000;
	}
}

/** Read the current cumulative timings (no reset). */
export function snapshot(): Record<string, TimingBucket> {
	return Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, {...v}]));
}

/** Reset all accumulators (call before a measured window). */
export function resetTiming(): void {
	for (const k of Object.keys(buckets)) delete buckets[k];
	totalCalls = 0;
}

/** Log the cumulative-since-reset timings and reset. */
export function flush(): void {
	const snap = snapshot();
	const lines = Object.entries(snap)
		.sort((a, b) => b[1].us - a[1].us)
		.map(([k, v]) => `  ${v.ms.toFixed(1).padStart(10)} ms  ${v.calls.toString().padStart(8)} calls  ${k}`);
	console.log(`[SceneryTiming] cumulative since last flush:\n${lines.join("\n")}`);
	resetTiming();
}

/** Auto-flush to the console every `n` total calls. 0 disables auto-flush. */
export function setAutoFlushEvery(n: number): void { autoFlushEvery = n; }

// ── expose to the console (mirrors the other `window.lf*Debug` hooks) ──
declare global {
	interface Window {
		lfSceneryTiming?: {
			dump(): Record<string, TimingBucket>;
			reset(): void;
			flush(): void;
			setAutoFlushEvery(n: number): void;
			enabled: boolean;
		};
	}
}
if (typeof window !== "undefined") {
	window.lfSceneryTiming = {
		dump: snapshot,
		reset: resetTiming,
		flush,
		setAutoFlushEvery,
		get enabled() { return sceneryTimingEnabled; },
	};
}
